<template>
  <div class="f-main bgImg container student-home">
    <div class="box" :style="wrapperStyle">
      <!-- 欢迎横幅：渐变背景、统计与底部操作条 -->
      <div class="hero-banner">
        <div class="hero-main">
          <div class="hero-left">
            <div class="hero-icon-box">
              <i class="el-icon-notebook-2" aria-hidden="true" />
            </div>
            <div class="hero-text">
              <h1 class="hero-title">{{ $t('studentHomepage.welcome') }}</h1>
              <p v-if="userInfo.role === 'S' && displayName" class="hero-sub">
                {{ $t('studentHomepage.welcomeSubtitle', { name: displayName }) }}
              </p>
              <p v-else-if="userInfo.role === 'S'" class="hero-sub">
                {{ $t('studentHomepage.welcome3') }}
              </p>
              <div v-else class="hero-sub hero-sub-parent">
                <span>{{ $t('studentHomepage.welcome1') }}</span>
                <span class="hero-name">{{ $route.query.studentName }}</span>
                <span>{{ $t('studentHomepage.welcome2') }}</span>
              </div>
            </div>
          </div>
          <div class="hero-stats">
            <div class="hero-stat">
              <span class="hero-stat-num">{{ overallPercentDisplay }}%</span>
              <span class="hero-stat-label">{{ $t('studentHomepage.statOverall') }}</span>
            </div>
            <div class="hero-stat">
              <span class="hero-stat-num">{{ categoriesList.length }}</span>
              <span class="hero-stat-label">{{ $t('studentHomepage.statSubjects') }}</span>
            </div>
            <div class="hero-stat">
              <span class="hero-stat-num">{{ activeSubjectsCount }}</span>
              <span class="hero-stat-label">{{ $t('studentHomepage.statActive') }}</span>
            </div>
          </div>
          <div v-if="isCurrentStudent" class="hero-grade-switcher">
            <el-popover
              v-model="gradeMenuVisible"
              placement="bottom-end"
              trigger="click"
              :width="300"
              popper-class="student-grade-popover"
              :disabled="gradesLoading || !gradeOptions.length"
            >
              <div class="grade-menu" :aria-label="$t('studentHomepage.switchLearningGrade')">
                <div v-if="currentGrade" class="grade-menu-section-label">
                  {{ $t('studentHomepage.learningNow') }}
                </div>
                <button
                  v-if="currentGrade"
                  type="button"
                  class="grade-menu-item is-current"
                  disabled
                >
                  <i :class="gradeStatusIcon(currentGrade)" aria-hidden="true" />
                  <span class="grade-menu-name">{{ gradeLabel(currentGrade) }}</span>
                </button>
                <div v-if="otherGradeOptions.length" class="grade-menu-divider" />
                <div v-if="otherGradeOptions.length" class="grade-menu-section-label">
                  {{ $t('studentHomepage.otherGrades') }}
                </div>
                <button
                  v-for="grade in otherGradeOptions"
                  :key="grade.gradeId"
                  type="button"
                  class="grade-menu-item"
                  :class="{ 'is-unbound': grade.status === 'UNBOUND' }"
                  :disabled="switchingGradeId !== null"
                  @click="selectGrade(grade)"
                >
                  <i :class="gradeStatusIcon(grade)" aria-hidden="true" />
                  <span class="grade-menu-copy">
                    <span class="grade-menu-name">{{ gradeLabel(grade) }}</span>
                    <span v-if="grade.status === 'UNBOUND'" class="grade-menu-hint">
                      {{ $t('studentHomepage.setupGradeHint') }}
                    </span>
                  </span>
                  <span class="grade-menu-action">
                    {{ $t(grade.status === 'BOUND'
                      ? 'studentHomepage.switchGradeAction'
                      : 'studentHomepage.setupGradeAction') }}
                  </span>
                </button>
              </div>
              <button
                slot="reference"
                type="button"
                class="grade-trigger"
                :class="{ 'is-loading': gradesLoading || switchingGradeId !== null }"
                :aria-label="$t('studentHomepage.switchLearningGrade')"
              >
                <i
                  v-if="gradesLoading || switchingGradeId !== null"
                  class="el-icon-loading"
                  aria-hidden="true"
                />
                <span>{{ currentGradeLabel }}</span>
                <i v-if="!gradesLoading && switchingGradeId === null" class="el-icon-arrow-down" aria-hidden="true" />
              </button>
            </el-popover>
            <span class="current-grade-caption">{{ $t('studentHomepage.currentGrade') }}</span>
          </div>
        </div>
      </div>

      <div class="subjects-head">
        <div class="subjects-head-left">
          <span class="subjects-tab">{{ $t('studentHomepage.allSubjects') }}</span>
          <span class="subjects-count">{{ filteredCategoriesList.length }} {{ $t('studentHomepage.totalLabel') }}</span>
        </div>
      </div>

      <div id="subjects-section" v-loading="categoriesLoading" class="content">
        <div
          v-for="item in filteredCategoriesList"
          :key="item.id"
          class="card"
        >
          <div
            class="card-icon-wrap"
            :style="{ background: categoryTheme(item).iconBg }"
          >
            <img
              :src="
                item.iconUrl
                  ? require(`@/assets/image/categoryImg/${item.iconUrl}.png`)
                  : require('@/assets/image/categoryImg/COMPREHENSIVE.png')
              "
              alt=""
            />
          </div>
          <div class="category">
            {{
              $i18n.locale === "en" ? item.categoryName : item.categoryNameFr
            }}
          </div>
          <div class="description">
            {{ $i18n.locale === "en" ? item.description : item.descriptionFr }}
          </div>
          <div class="progress">
            <span v-if="item.overallProgress * 100 === 0" class="INProgress">{{
              $t("studentHomepage.INProgress")
            }}</span>
            <span
              v-else-if="item.overallProgress * 100 === 100"
              class="Completed"
              >{{ $t("studentHomepage.Completed") }}</span
            >
            <span v-else class="InProgress">{{
              $t("studentHomepage.InProgress")
            }}</span>
            <span class="overallProgress"
              >{{ (item.overallProgress * 100).toFixed(2) }}%</span
            >
          </div>
          <el-progress
            :percentage="Math.min(100, item.overallProgress * 100)"
            :show-text="false"
            :stroke-width="8"
            :color="categoryTheme(item).accent"
            class="card-progress"
          />
          <div class="type">
            <span
              class="pill Basic"
              :class="{ 'pill-active': levelFilter === '1' }"
              @click.stop="toView(item, 1)"
              v-if="item.easyCount > 0"
            >
              {{ $t("studentHomepage.Basic") }}
            </span>
            <span
              class="pill Intermediate"
              :class="{ 'pill-active': levelFilter === '2' }"
              @click.stop="toView(item, 2)"
              v-if="item.mediumCount > 0"
            >
              {{ $t("studentHomepage.Intermediate") }}
            </span>
            <span
              class="pill Advanced"
              :class="{ 'pill-active': levelFilter === '3' }"
              @click.stop="toView(item, 3)"
              v-if="item.hardCount > 0"
            >
              {{ $t("studentHomepage.Advanced") }}
            </span>
          </div>
        </div>
      </div>

    </div>
    <div class="footer">{{ $t("common.footer") }}</div>
    <div class="start-Details" v-if="startDialog">
      <div class="info">
        <i class="close el-icon-close" @click="close()" />
        <div class="categoriesInfo">
          <img
            :src="selectCategories.iconUrl
                ? require(`@/assets/image/categoryImg/${selectCategories.iconUrl}.png`)
                : require('@/assets/image/categoryImg/COMPREHENSIVE.png')"
            alt=""
          />
          <div class="categories">
            <div class="category">
              {{
                $i18n.locale === "en" ? selectCategories.categoryName : selectCategories.categoryNameFr
              }}
            </div>
            <div class="description">
              {{ $i18n.locale === "en" ? selectCategories.description : selectCategories.descriptionFr }}
            </div>
          </div>
        </div>
        <hr class="hr"/>
        <div class="buttons">
          <div class="top">
            <el-select v-model="pointCode" :placeholder="$t('studentHomepage.placeholder')" clearable>
              <el-option
                  v-for="item in pointList"
                  :key="item.id"
                  :label="$i18n.locale === 'en' ? item.pointName : item.pointNameFr"
                  :value="item.id">
              </el-option>
            </el-select>
            <div
                class="historical"
                @click="historical"
                :style="pointCode ? '' : 'cursor:not-allowed'"
            >
              <i class="el-icon-time" />
              <span>{{ $t("studentHomepage.historical-test") }}</span>
            </div>
          </div>
          <div class="line">Choose a Learning Mode</div>
          <div class="bottom">
            <div class="learn-box" @click="toLearn()" :style="pointCode ? '' : 'cursor:not-allowed'">
              <img
                  :src="require('@/assets/image/studentHomepage/learn.png')"
                  alt=""
              />
              <span class="title">{{ $t("studentHomepage.learn") }}</span>
              <span class="detailed">{{ $t("studentHomepage.learnDetailed") }}</span>
            </div>
            <div class="quiz-box" @click="toQuiz()" :style="pointCode ? '' : 'cursor:not-allowed'">
              <img
                :src="require('@/assets/image/studentHomepage/quiz.png')"
                alt=""
              />
              <span class="title">{{ $t("studentHomepage.quiz") }}</span>
              <span class="detailed">{{ $t("studentHomepage.quizDetailed") }}</span>
            </div>
          </div>
          <div class="text">Choose a math concept to begin learning.</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import {
  boundCategories,
  getStudentGradeOptions,
  getpointList,
  switchStudentGrade
} from '@/api/tutor/student/index.js'

export default {
  name: 'studentHomepage',
  props: {
    scale: {
      type: Number,
      required: 1
    }
  },
  data () {
    return {
      userId: null,
      activeGradeId: null,
      gradeOptions: [],
      gradeMenuVisible: false,
      gradesLoading: false,
      categoriesLoading: false,
      switchingGradeId: null,
      categoriesList: [],
      startDialog: false,
      selectCategories: null,
      pointCode: '',
      pointList: [],
      /** @type {'all'|'1'|'2'|'3'} */
      levelFilter: 'all'
    }
  },
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userInfo
    }),
    wrapperStyle () {
      return {
        transform: `scale(${this.scale})`
      }
    },
    /**
     * 展示用用户名（路由学生名或当前账号）
     * @returns {string}
     */
    displayName () {
      const q = this.$route.query.studentName
      if (q) {
        return String(q)
      }
      if (!this.userInfo) {
        return ''
      }
      return this.userInfo.userAccount || this.userInfo.username || ''
    },
    isCurrentStudent () {
      return this.userInfo &&
        this.userInfo.role === 'S' &&
        String(this.userId) === String(this.userInfo.id)
    },
    currentGrade () {
      return this.gradeOptions.find((grade) => grade.status === 'CURRENT') ||
        this.gradeOptions.find((grade) => String(grade.gradeId) === String(this.activeGradeId)) ||
        null
    },
    otherGradeOptions () {
      return this.gradeOptions
        .filter((grade) => !this.currentGrade || grade.gradeId !== this.currentGrade.gradeId)
        .sort((left, right) => {
          const statusOrder = { BOUND: 0, UNBOUND: 1 }
          const statusDifference = (statusOrder[left.status] || 0) - (statusOrder[right.status] || 0)
          return statusDifference || left.gradeLevel - right.gradeLevel
        })
    },
    currentGradeLabel () {
      if (this.currentGrade) {
        return this.gradeLabel(this.currentGrade)
      }
      const legacyGrade = this.userInfo && this.userInfo.grade
      return legacyGrade
        ? this.$t('studentHomepage.gradeOption', { grade: legacyGrade })
        : this.$t('studentHomepage.grade')
    },
    /**
     * 按难度筛选后的学科列表
     * @returns {Array}
     */
    filteredCategoriesList () {
      const list = this.categoriesList || []
      const availableList = list.filter((c) => {
        const easy = Number(c.easyCount) || 0
        const medium = Number(c.mediumCount) || 0
        const hard = Number(c.hardCount) || 0
        return easy > 0 || medium > 0 || hard > 0
      })
      if (this.levelFilter === 'all') {
        return availableList
      }
      const d = this.levelFilter
      return availableList.filter((c) => {
        if (d === '1') {
          return (Number(c.easyCount) || 0) > 0
        }
        if (d === '2') {
          return (Number(c.mediumCount) || 0) > 0
        }
        return (Number(c.hardCount) || 0) > 0
      })
    },
    /**
     * 全学科平均进度（百分比，保留一位小数）
     * @returns {string}
     */
    overallPercentDisplay () {
      const list = this.categoriesList || []
      if (!list.length) {
        return '0'
      }
      const sum = list.reduce((acc, c) => acc + (Number(c.overallProgress) || 0), 0)
      const avg = (sum / list.length) * 100
      return avg.toFixed(1)
    },
    /**
     * 进行中（进度介于 0 与 100% 之间）的学科数量
     * @returns {number}
     */
    activeSubjectsCount () {
      return (this.categoriesList || []).filter((c) => {
        const p = Number(c.overallProgress) || 0
        return p > 0 && p < 1
      }).length
    }
  },
  mounted () {
    console.log('%c FXY', 'color: #46cef9;font-size:37px', this.scale)
    this.userId = this.$route.query.userId || this.userInfo.id
    this.activeGradeId = this.$route.query.gradeId || this.userInfo.activeGradeId || null
    this.initializeHomepage()
  },
  methods: {
    async initializeHomepage () {
      await this.loadGradeOptions()
      await this.loadCategories()
    },
    async loadGradeOptions () {
      this.gradesLoading = true
      try {
        const res = await getStudentGradeOptions(this.userId)
        if (res.code !== 200) {
          return
        }
        this.gradeOptions = Array.isArray(res.data) ? res.data : []
        const current = this.gradeOptions.find((grade) => grade.status === 'CURRENT')
        if (current) {
          this.activeGradeId = current.gradeId
        }
      } catch (error) {
        this.gradeOptions = []
        if (!this.activeGradeId) {
          this.activeGradeId = this.userInfo && this.userInfo.grade
        }
      } finally {
        this.gradesLoading = false
      }
    },
    async loadCategories () {
      this.categoriesLoading = true
      try {
        const res = await boundCategories(this.userId, this.activeGradeId)
        if (res.code !== 200) {
          return
        }
        this.categoriesList = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        this.categoriesList = []
      } finally {
        this.categoriesLoading = false
      }
    },
    gradeLabel (grade) {
      return this.$t('studentHomepage.gradeOption', { grade: grade.gradeLevel })
    },
    gradeStatusIcon (grade) {
      if (this.switchingGradeId === grade.gradeId) {
        return 'el-icon-loading grade-icon-current'
      }
      if (grade.status === 'CURRENT') {
        return 'el-icon-success grade-icon-current'
      }
      if (grade.status === 'BOUND') {
        return 'el-icon-success grade-icon-bound'
      }
      return 'el-icon-circle-plus-outline grade-icon-unbound'
    },
    async selectGrade (grade) {
      if (!grade || grade.status === 'CURRENT' || this.switchingGradeId !== null) {
        return
      }
      this.gradeMenuVisible = false
      if (grade.status === 'UNBOUND') {
        try {
          await this.$confirm(
            this.$t('studentHomepage.setupGradeConfirm', { grade: this.gradeLabel(grade) }),
            this.$t('studentHomepage.setupGradeTitle'),
            {
              confirmButtonText: this.$t('studentHomepage.confirmSetupGrade'),
              cancelButtonText: this.$t('studentHomepage.cancelGradeSwitch'),
              type: 'warning',
              customClass: 'student-grade-confirm',
              closeOnClickModal: false
            }
          )
        } catch (action) {
          return
        }
      }

      this.switchingGradeId = grade.gradeId
      try {
        const res = await switchStudentGrade(this.userId, grade.gradeId)
        if (res.code !== 200) {
          this.$message.error(this.$t('studentHomepage.gradeSwitchFailed'))
          return
        }
        this.activeGradeId = grade.gradeId
        await Promise.all([this.loadGradeOptions(), this.loadCategories()])
        this.$message.success(
          res.data && res.data.newlyBound
            ? this.$t('studentHomepage.gradeSetupSuccess', { grade: this.gradeLabel(grade) })
            : this.$t('studentHomepage.gradeSwitchSuccess', { grade: this.gradeLabel(grade) })
        )
      } catch (error) {
        this.$message.error(this.$t('studentHomepage.gradeSwitchFailed'))
      } finally {
        this.switchingGradeId = null
      }
    },
    /**
     * 根据后端 icon 文件名返回主题色（紫 / 粉 / 橙 / 青）
     * @param {{ iconUrl?: string }} item 学科项
     * @returns {{ accent: string, iconBg: string }}
     */
    categoryTheme (item) {
      const key = (item && item.iconUrl ? String(item.iconUrl) : '').toUpperCase()
      if (key.includes('GEOM')) {
        return {
          accent: '#ec4899',
          iconBg: 'linear-gradient(145deg, #f9a8d4 0%, #db2777 100%)'
        }
      }
      if (key.includes('STAT')) {
        return {
          accent: '#ff8a00',
          iconBg: 'linear-gradient(145deg, #fdba74 0%, #ea580c 100%)'
        }
      }
      if (key.includes('COMP')) {
        return {
          accent: '#14b8a6',
          iconBg: 'linear-gradient(145deg, #5eead4 0%, #0d9488 100%)'
        }
      }
      return {
        accent: '#7c4dff',
        iconBg: 'linear-gradient(145deg, #c4b5fd 0%, #6d28d9 100%)'
      }
    },
    /**
     * 滚动到学科卡片区域
     */
    scrollToSubjects () {
      const el = document.getElementById('subjects-section')
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    /**
     * 按等级筛选学科卡片
     * @param {'all'|'1'|'2'|'3'} cmd
     */
    onLevelFilter (cmd) {
      this.levelFilter = cmd
    },
    toView (item, type) {
      this.selectCategories = item
      this.$set(this.selectCategories, 'difficultyLevel', type)
      this.getpointList()
      this.startDialog = true
    },
    getpointList () {
      const params = {
        categoryId: this.selectCategories.id,
        difficultyLevel: this.selectCategories.difficultyLevel
      }
      getpointList(params).then((res) => {
        if (res.code !== 200) {
          return false
        }
        this.pointList = res.data
      })
    },
    close () {
      this.selectCategories = null
      this.startDialog = false
    },
    toLearn (item) {
      if (!this.pointCode) {
        this.$message.warning(this.$t('studentHomepage.message'))
        return false
      }
      const gradeId = item ? item.gradeId : this.selectCategories.gradeId
      const categoryIds = item ? item.id : this.selectCategories.id
      const studentName = this.$route.query.studentName || ''
      this.$router.push({
        path: '/studentLearn',
        query: {
          studentId: this.userId,
          gradeId: gradeId,
          knowledgePointId: this.pointCode,
          categoryId: categoryIds,
          studentName: studentName,
          difficultyLevel: this.selectCategories.difficultyLevel
        }
      })
    },
    toQuiz (item) {
      if (!this.pointCode) {
        this.$message.warning(this.$t('studentHomepage.message'))
        return false
      }
      this.$router.push({
        path: '/studentQuiz',
        query: {
          type: 'quiz',
          studentId: this.userId,
          gradeId: this.selectCategories.gradeId,
          categoryIds: this.selectCategories.id,
          knowledgePointId: this.pointCode,
          difficultyLevel: this.selectCategories.difficultyLevel
        }
      })
    },
    toHome () {
      this.$router.push({
        path: '/parentOrTeacher'
      })
    },
    historical () {
      if (!this.pointCode) {
        this.$message.warning(this.$t('studentHomepage.message'))
        return false
      }
      this.$router.push({
        path: '/quizHistorical',
        query: {
          studentId: this.userId,
          knowledgePointId: this.pointCode
        }
      })
    }
  }
}
</script>

<style scoped>
.student-home {
  --sh-purple: #7c4dff;
  --sh-purple-deep: #5b21b6;
  --sh-orange: #ff8a00;
  --sh-lavender: #ede7f6;
  --sh-text-muted: #94a3b8;
  --sh-card-radius: 16px;
}

.student-home.container {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: calc(100% - 90px);
  padding: 12px 16px;
  box-sizing: border-box;
}

.page-tagline {
  width: 100%;
  max-width: 1480px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.12em;
  color: #fff;
  background: var(--sh-purple-deep);
  padding: 8px 16px;
  border-radius: 999px;
  margin-bottom: 12px;
}

.student-home .footer {
  margin-top: 16px;
  font-weight: 400;
  font-size: 11px;
  color: #94a3b8;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
}

.student-home .box {
  width: 100%;
  max-width: 1443px;
  max-height: none;
  min-height: 720px;
  border-radius: var(--sh-card-radius);
  display: flex;
  flex-direction: column;
  padding: 20px 20px 24px;
}

/* 欢迎横幅 */
.hero-banner {
  border-radius: var(--sh-card-radius);
  overflow: hidden;
  margin-bottom: 24px;
  box-shadow: 0 12px 40px rgba(91, 33, 182, 0.2);
}

.hero-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 48px 32px;
  background: linear-gradient(115deg, #7c4dff 0%, #6366f1 45%, #2563eb 100%);
}

.hero-left {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  flex: 1;
  min-width: 240px;
}

.hero-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 26px;
  flex-shrink: 0;
}

.hero-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
}

.hero-sub {
  margin: 8px 0 0;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.45;
}

.hero-sub-parent .hero-name {
  font-weight: 700;
  margin: 0 4px;
}

.hero-stats {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.hero-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 72px;
}

.hero-stat-num {
  font-size: 26px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
}

.hero-stat-label {
  margin-top: 6px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-transform: capitalize;
}

.hero-grade-switcher {
  width: 138px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}

.grade-trigger {
  width: 138px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s, transform 0.2s;
}

.grade-trigger:hover,
.grade-trigger:focus-visible {
  outline: none;
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.14);
  transform: translateY(-1px);
}

.grade-trigger.is-loading {
  cursor: wait;
}

.current-grade-caption {
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  font-weight: 500;
}

.hero-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  background: rgba(49, 20, 120, 0.45);
  backdrop-filter: blur(6px);
}

.strip-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
}

.strip-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 10px;
  transition: background 0.2s;
}

.strip-link:hover {
  background: rgba(255, 255, 255, 0.12);
}

.strip-link i {
  font-size: 16px;
}

.btn-continue-learning {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  border: none;
  border-radius: 999px;
  background: var(--sh-orange);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(255, 138, 0, 0.45);
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-continue-learning:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(255, 138, 0, 0.5);
}

.btn-continue-learning i {
  font-size: 18px;
}

/* 学科区标题 */
.subjects-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin: 0 4px 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(124, 77, 255, 0.15);
}

.subjects-head-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}

.subjects-tab {
  font-size: 18px;
  font-weight: 800;
  color: var(--sh-purple-deep);
  padding-bottom: 4px;
  border-bottom: 4px solid var(--sh-purple);
}

.subjects-count {
  font-size: 14px;
  color: var(--sh-text-muted);
  font-weight: 500;
}

.filter-by-level {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--sh-purple);
  cursor: pointer;
  user-select: none;
}

.filter-by-level:hover {
  opacity: 0.85;
}

.student-home .content {
  margin: 0 4px;
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
  padding: 4px 0 8px;
  gap: 20px;
  align-content: flex-start;
  justify-content: flex-start;
}

.student-home .card {
  width: calc(100% / 3 - 14px);
  min-width: 260px;
  flex: 1 1 calc(33.333% - 14px);
  max-width: calc(33.333% - 14px);
  min-height: 320px;
  background: #ffffff;
  padding: 22px 20px 18px;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-radius: var(--sh-card-radius);
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.9);
  cursor: default;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.student-home .card:hover {
  transform: translateY(-4px);
  box-shadow: 0 16px 40px rgba(91, 33, 182, 0.12);
}

.card-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.card-icon-wrap img {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.student-home .card .category {
  margin: 18px 0 8px;
  min-height: 24px;
  font-weight: 800;
  font-size: 22px;
  color: #0f172a;
  text-align: center;
}

.student-home .card .description {
  font-weight: 400;
  font-size: 14px;
  color: #64748b;
  line-height: 1.45;
  max-height: 44px;
  overflow: hidden;
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.student-home .card .progress {
  margin: 18px 0 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.student-home .card .INProgress {
  font-weight: 700;
  font-size: 14px;
  color: #585f6a;
}

.student-home .card .Completed {
  font-weight: 700;
  font-size: 14px;
  color: #16a34a;
}

.student-home .card .InProgress {
  font-weight: 700;
  font-size: 14px;
  color: #b45309;
}

.student-home .card .overallProgress {
  font-weight: 600;
  font-size: 14px;
  color: rgb(109, 40, 217);
}

.card-progress {
  width: 100%;
}

.card-progress ::v-deep .el-progress-bar__outer {
  border-radius: 999px;
  background-color: #f1f5f9;
}

.card-progress ::v-deep .el-progress-bar__inner {
  border-radius: 999px;
}

.student-home .card .type {
  margin-top: 14px;
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.student-home .card .type .pill {
  border-radius: 999px;
  padding: 6px 14px;
  min-width: 0;
  text-align: center;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.student-home .card .type .Basic {
  background: #f3e8ff;
  color: #7c3aed;
}

.student-home .card .type .Intermediate {
  background: #fce7f3;
  color: #be185d;
}

.student-home .card .type .Advanced {
  color: #b91c1c;
  background: #fee2e2;
}

.student-home .card .type .pill.pill-active {
  box-shadow: 0 0 0 2px rgba(124, 77, 255, 0.35);
}

/* 底部 CTA */
.cta-bar {
  margin-top: 20px;
  padding: 20px 24px;
  border-radius: var(--sh-card-radius);
  background: var(--sh-lavender);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid rgba(124, 77, 255, 0.12);
}

.cta-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 200px;
}

.cta-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(145deg, #8b5cf6, #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 24px;
  flex-shrink: 0;
}

.cta-title {
  margin: 0;
  font-size: 17px;
  font-weight: 800;
  color: #1e1b4b;
}

.cta-sub {
  margin: 4px 0 0;
  font-size: 14px;
  color: #64748b;
  line-height: 1.4;
}

.btn-view-plan {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(90deg, #7c4dff 0%, #5b21b6 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.2s, box-shadow 0.2s;
}

.btn-view-plan:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 24px rgba(91, 33, 182, 0.35);
}

@media (max-width: 1200px) {
  .student-home .card {
    max-width: calc(50% - 10px);
    flex: 1 1 calc(50% - 10px);
  }
}

@media (max-width: 720px) {
  .hero-stats {
    width: 100%;
    justify-content: space-between;
  }

  .student-home .card {
    max-width: 100%;
    flex: 1 1 100%;
  }

  .hero-grade-switcher {
    width: 100%;
    align-items: flex-end;
  }
}

  .start-Details{
    width: 100%;
    height: calc(100% - 90px);
    position: absolute;
    top: 90px;
    z-index: 40;
    background: rgba(18, 16, 44, 0.45);
    backdrop-filter: blur(5px);
    display: flex;
    justify-content: center;
    align-items: center;
    .info {
      width: 560px;
      min-height: 530px;
      background: #ffffff;
      box-shadow: 0 22px 60px rgba(37, 24, 78, 0.28);
      border-radius: 22px;
      display: flex;
      flex-direction: column;
      padding-bottom: 26px;
      align-items: center;
      position: relative;
      overflow: hidden;
      .close {
        position: absolute;
        right: 14px;
        font-size: 16px;
        top: 14px;
        color: rgba(255, 255, 255, 0.9);
        width: 22px;
        height: 22px;
        line-height: 22px;
        text-align: center;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.18);
        cursor: pointer;
        z-index: 2;
      }
      .close:hover {
        background: rgba(255, 255, 255, 0.3);
      }
      .categoriesInfo{
        display: flex;
        width: 100%;
        align-items: center;
        margin: 0;
        padding: 42px 30px 32px;
        background: linear-gradient(136deg, #4f46e5 0%, #7c3aed 55%, #8b5cf6 100%);
        position: relative;
        box-sizing: border-box;
        &::before{
          content: "AI Learning";
          position: absolute;
          top: 12px;
          left: 24px;
          height: 24px;
          line-height: 24px;
          border-radius: 999px;
          padding: 0 12px;
          font-weight: 600;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.95);
          background: rgba(255, 255, 255, 0.2);
        }
        img{
          width: 64px;
          height: 64px;
          display: inline-block;
          border-radius: 14px;
          background: rgba(255, 255, 255, 0.12);
          padding: 10px;
          box-sizing: border-box;
        }
      }
      .categories{
        margin-left: 18px;
        .category {
          margin: 0 0 8px;
          font-weight: bold;
          font-size: 31px;
          line-height: 1.15;
          color: #ffffff;
        }

        .description {
          font-weight: 400;
          font-size: 15px;
          color: rgba(255, 255, 255, 0.82);
          line-height: 1.45;
          max-height: 44px;
          overflow: hidden;
        }
      }
      .hr{
        display: none;
      }
      .buttons{
        padding: 20px 28px 0;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        width: 100%;
        box-sizing: border-box;

        .top,
        .bottom{
          display: flex;
          gap: 12px;
          width: 100%;
        }
        .top{
          position: relative;
          .el-select{
            width: 65%;
          }
          .historical{
            width: 35%;
            box-shadow: 0 8px 20px rgba(244, 183, 77, 0.18);
            border-radius: 12px;
            border: 1px solid #f3e6c7;
            background: #fff8e6;
            height: 56px;
            margin-bottom: 14px;
            padding: 0 14px;
            font-weight: 700;
            font-size: 16px;
            color: #7d5a1f;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            i{
              font-size: 16px;
              color: #f59e0b;
            }
          }
        }
        .line{
          width: 100%;
          text-align: center;
          font-size: 12px;
          letter-spacing: 1.8px;
          color: #9a93bf;
          font-weight: 700;
          margin: 10px 0 12px;

        }
        .text{
          width: 100%;
          text-align: center;
          font-size: 12px;
          letter-spacing: 1.8px;
          color: #9a93bf;
          font-weight: 700;
          margin: 26px 0 0;
        }
        .learn-box,
        .quiz-box{
          width: 100%;
          min-height: 174px;
          background: #f3f5ff;
          border: 1px solid #ebecf8;
          box-shadow: 0 8px 18px rgba(36, 27, 79, 0.08);
          border-radius: 14px;
          display: flex;
          flex-direction: column;
          align-items: center;
          cursor: pointer;
          padding: 6px 0 14px;
          box-sizing: border-box;
          transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
          img{
            margin: 16px 0 14px;
            height: 42px;
          }
          .title{
            font-weight: 700;
            font-size: 22px;
            color: #232323;
            margin-bottom: 8px;
          }
          .detailed{
            min-height: 40px;
            font-weight: 400;
            font-size: 13px;
            color: #8f8f9f;
            line-height: 1.4;
            margin: 0 20px;
            text-align: center;
          }
          &:hover{
            box-shadow: 0 12px 24px rgba(56, 45, 113, 0.14);
            transform: translateY(-1px);
            .title{
              color: #3d2d89;
            }
            .detailed{
              color: #6a6792;
            }
          }
        }
        .learn-box{
          background: #F2F2FF;
        }
        .quiz-box{
          background: #FAF2FA;
        }
      }

    }
}
.buttons .top .el-select ::v-deep .el-input__inner::placeholder{
  font-weight: 400;
  font-size: 18px;
  color: #332f4c;
}
.buttons .top .el-select ::v-deep .el-input__inner{
  box-shadow: 0 8px 18px rgba(36, 27, 79, 0.08);
  border-radius: 12px;
  border: 1px solid #e9e8f4;
  height: 56px;
  line-height: 56px;
  font-weight: 600;
  font-size: 18px;
  color: #332f4c;
}
.buttons .top .el-select ::v-deep .el-input__icon{
  line-height: 44px;
}
.buttons .top .el-select ::v-deep .el-input__inner:hover{
  border-color: #d8d4f0;
}
</style>

<style>
.student-grade-popover {
  padding: 0 !important;
  border: 1px solid #eee9fb !important;
  border-radius: 16px !important;
  box-shadow: 0 18px 48px rgba(55, 35, 105, 0.2) !important;
  overflow: hidden;
}

.student-grade-popover .popper__arrow {
  border-bottom-color: #eee9fb !important;
}

.grade-menu {
  padding: 14px 12px 12px;
  background: #fff;
}

.grade-menu-section-label {
  padding: 2px 10px 9px;
  color: #7c3aed;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.14em;
  line-height: 1.3;
  text-transform: uppercase;
}

.grade-menu-divider {
  height: 1px;
  margin: 9px 4px 12px;
  background: #ede9f5;
}

.grade-menu-item {
  width: 100%;
  min-height: 48px;
  padding: 7px 10px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 8px;
  color: #1f2937;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.16s;
}

.grade-menu-item:hover:not(:disabled),
.grade-menu-item:focus-visible {
  outline: none;
  background: #f7f3ff;
}

.grade-menu-item.is-current {
  background: #f4efff;
  color: #5b21b6;
}

.grade-menu-item:disabled {
  cursor: default;
}

.grade-menu-name {
  display: block;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.35;
}

.grade-menu-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.grade-menu-hint {
  color: #8290a3;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.25;
}

.grade-menu-action {
  color: #6d28d9;
  font-size: 12px;
  font-weight: 750;
  white-space: nowrap;
}

.grade-icon-current,
.grade-icon-bound {
  color: #16a34a;
  font-size: 17px;
}

.grade-icon-current {
  color: #6d28d9;
}

.grade-icon-unbound {
  color: #7c3aed;
  font-size: 18px;
}

.student-grade-confirm {
  border-radius: 16px !important;
  overflow: hidden;
}
</style>
